import { PlusIcon } from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import { GetExpenseResponse, GetPayUpType, GetUserType } from '@orbit/api-types'
import React from 'react'
import { ActivityFilter, isFilterActive } from '../ActivityFilterModal'
import { ActivityDateGroup } from '../useGroupedActivity'
import ActivityFeed from './ActivityFeed'
import {
	ExportButton,
	FilterButton,
	FilterCountBadge,
	HeaderActionButton,
	SectionBody,
	SectionHeader,
	SectionHeaderActions,
	SectionRoot,
	SectionTitle,
} from './ActivitySection.styles'
import { TestIds } from '@/testIds'

interface Props {
	groupedActivity: ActivityDateGroup[]
	activityFilter: ActivityFilter
	activityFilterCount: number
	members: GetUserType[]
	me: GetUserType | undefined
	fileUrlById: Map<string, string>
	showCurrencyCode: boolean
	onOpenFilter: () => void
	onExportCsv: () => void
	canExportCsv: boolean
	onAddExpense: () => void
	onClickExpense: (expense: GetExpenseResponse) => void
	onClickPayUp: (payUp: GetPayUpType) => void
}

/**
 * The member Balances activity panel: the export/filter/add header around the
 * shared `ActivityFeed` (which the public expenses page renders read-only).
 */
const ActivitySection: React.FC<Props> = ({
	groupedActivity,
	activityFilter,
	activityFilterCount,
	members,
	me,
	fileUrlById,
	showCurrencyCode,
	onOpenFilter,
	onExportCsv,
	canExportCsv,
	onAddExpense,
	onClickExpense,
	onClickPayUp,
}) => (
	<SectionRoot>
		<SectionHeader>
			<SectionTitle>Activity</SectionTitle>
			<SectionHeaderActions>
				<ExportButton type="button" onClick={onExportCsv} disabled={!canExportCsv}>
					Export CSV
				</ExportButton>
				<FilterButton
					type="button"
					$active={isFilterActive(activityFilter)}
					onClick={onOpenFilter}
				>
					Filter
					{activityFilterCount > 0 && (
						<FilterCountBadge>{activityFilterCount}</FilterCountBadge>
					)}
				</FilterButton>
				<HeaderActionButton type="button" onClick={onAddExpense} data-testid={TestIds.common.fab}>
					<PlusIcon sx={{ fontSize: iconSizes.sm }} />
					Add Expense
				</HeaderActionButton>
			</SectionHeaderActions>
		</SectionHeader>
		<SectionBody>
			<ActivityFeed
				groupedActivity={groupedActivity}
				people={members}
				me={me}
				fileUrlById={fileUrlById}
				showCurrencyCode={showCurrencyCode}
				empty={
					isFilterActive(activityFilter)
						? {
								title: 'No matching activity',
								description: 'No activity matches the current filter.',
							}
						: { title: 'There are no expenses or payups' }
				}
				onClickExpense={onClickExpense}
				onClickPayUp={onClickPayUp}
			/>
		</SectionBody>
	</SectionRoot>
)

export default ActivitySection
